import { useEffect, useCallback } from 'react';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { getSupabase, getSession, signInWithEmail, signUpWithEmail, signOut } from './supabaseClient';
import { useAuthStore } from '@/state/useAuthStore';

// Hook to manage authentication session
export function useSession() {
  const { 
    session, 
    user, 
    loading, 
    setSession, 
    setUser, 
    setLoading,
    clearAuth 
  } = useAuthStore();

  // Initialize session on mount
  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      try {
        const currentSession = await getSession();
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
        }
      } catch (error) {
        console.error('Failed to get session:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    // Listen for auth changes
    const supabase = getSupabase();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, newSession: Session | null) => {
        if (mounted) {
          setSession(newSession);
          setUser(newSession?.user ?? null);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setSession, setUser, setLoading]);

  // Auth actions
  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      await signInWithEmail(email, password);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const register = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      await signUpWithEmail(email, password);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await signOut();
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [setLoading, clearAuth]);

  return {
    session,
    user,
    loading,
    isAuthenticated: !!session,
    login,
    register,
    logout,
  };
}

// Export types
export type { Session, User };
